/* eslint-disable */
import { Button, Flex, Modal, Space, Table, Tooltip, Typography, message } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";

const PAGE_SIZE = 100;

const renderTime = (text) => (text ? moment(text).format("MM/DD/YYYY HH:mm") : " ");

const renderRoleList = (roleList) => {
  if (!roleList || roleList.length === 0) return " ";
  return (
    <Flex gap="4px 0" wrap="wrap">
      {roleList.map((item, idx) => (
        <Tooltip key={idx} title={item.roleName} className="my-tooltip">
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {item.roleName}
          </div>
        </Tooltip>
      ))}
    </Flex>
  );
};

// 主表和弹窗表共用的列
const buildColumns = (onEmailEnter, compact = false) => [
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    titleType: "Input",
    width: 300,
    align: "center",
    isSorter: true,
    sortterMultiple: 2,
    onChange: onEmailEnter,
  },
  {
    title: "Role",
    dataIndex: "roleList",
    key: "roleList",
    width: 200,
    align: "center",
    isSorter: true,
    sortterMultiple: 3,
    render: renderRoleList,
  },
  {
    title: "Last Login Time",
    dataIndex: "lastLoginTime",
    key: "lastLoginTime",
    width: 200,
    align: "center",
    isSorter: true,
    sortterMultiple: 3,
    render: renderTime,
  },
  {
    title: "Login Count",
    dataIndex: "loginCount",
    key: "loginCount",
    width: 300,
    align: "center",
    isSorter: true,
    sortterMultiple: 2,
  },
  {
    title: "Name",
    dataIndex: "allname",
    key: "allname",
    width: compact ? 150 : 300,
    align: "center",
    isSorter: true,
    sortterMultiple: 2,
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    width: compact ? 150 : 300,
    align: "center",
    isSorter: true,
    sortterMultiple: 2,
  },
];

// 从路由取参数(兼容 search 和 hash)
const getRouteParams = () => {
  let params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
  if (Object.keys(params).length === 0) {
    const [, queryString] = window.location.hash.slice(1).split("?");
    if (queryString) params = Object.fromEntries(new URLSearchParams(queryString).entries());
  }
  return params;
};

export default function RoleAccount() {
  const roleIdRef = useRef(null);
  const [roleData, setRoleData] = useState({});

  // ================= 主表(已分配人员) =================
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
  const [selectedKeys, setSelectedKeys] = useState([]);
  const pageRef = useRef({ current: 1, pageSize: PAGE_SIZE });
  const filterRef = useRef({ email: "" });

  const fetchData = async (page = 1, pageSize = pageRef.current.pageSize) => {
    setTableLoading(true);
    try {
      const email = filterRef.current.email;
      const response = await http.post(api.getAccountListByRoleId, {
        page,
        count: pageSize,
        roleId: roleIdRef.current,
        ...(email && { email }),
      });
      setTableData(response?.data?.list || []);
      setSelectedKeys([]);
      pageRef.current = { current: page, pageSize };
      setPagination({ current: page, pageSize, total: response?.data?.total || 0 });
    } catch (err) {
      console.error("请求失败:", err);
      message.error("Failed to load data.");
    } finally {
      setTableLoading(false);
    }
  };

  const handleMainEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    filterRef.current = { email: e.target.value };
    fetchData(1);
  };

  const columns = useMemo(() => commonFn.handleColunmFn(buildColumns(handleMainEnter)), []);

  const onDelete = () => {
    if (selectedKeys.length === 0) {
      message.warning("Please select at least one user.");
      return;
    }
    Modal.confirm({
      title: "Delete Reminder",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure to remove the selected users from this role?",
      okText: "Sure",
      cancelText: "Cancel",
      style: { top: "40%" },
      onOk: async () => {
        try {
          const response = await http.post(api.getAccountDel, {
            roleId: roleIdRef.current,
            ids: selectedKeys.map((id) => String(id)),
          });
          if (response?.code == "1") {
            message.success("Successfully updated.");
            fetchData(pageRef.current.current, pageRef.current.pageSize);
          } else {
            setTimeout(() => commonFn.modalFailedFn(response), 500);
          }
        } catch (err) {
          message.error("An error occurred. Please try again.");
        }
      },
    });
  };

  // ================= 弹窗(未分配人员) =================
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otherData, setOtherData] = useState([]);
  const [otherLoading, setOtherLoading] = useState(false);
  const [otherPagination, setOtherPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });
  const otherPageRef = useRef({ current: 1, pageSize: PAGE_SIZE });
  const otherFilterRef = useRef({ email: "" });

  // 已勾选的人: accountId -> 整行记录。独立于当前列表,搜索/翻页都不会丢
  const [selectedMap, setSelectedMap] = useState(new Map());

  const fetchOtherList = async (page = 1, pageSize = otherPageRef.current.pageSize) => {
    setOtherLoading(true);
    try {
      const response = await http.post(api.getAccountOtherListByRoleId, {
        page,
        count: pageSize,
        roleId: roleIdRef.current,
        email: otherFilterRef.current.email || "",
      });
      setOtherData(response?.data?.list || []);
      otherPageRef.current = { current: page, pageSize };
      setOtherPagination({ current: page, pageSize, total: response?.data?.total || 0 });
    } catch (err) {
      console.error("请求失败:", err);
      message.error("Failed to load data.");
    } finally {
      setOtherLoading(false);
    }
  };

  const handleOtherEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    otherFilterRef.current = { email: e.target.value };
    fetchOtherList(1);
  };

  const otherColumns = useMemo(
    () => commonFn.handleColunmFn(buildColumns(handleOtherEnter, true)),
    []
  );

  const openModal = () => {
    otherFilterRef.current = { email: "" };
    setSelectedMap(new Map());
    setModalOpen(true);
    fetchOtherList(1);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMap(new Map());
  };

  // 只增删"本次操作的行",不覆盖其他已勾选的人
  const updateSelected = (rows, selected) => {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      rows.forEach((row) => {
        if (selected) next.set(row.accountId, row);
        else next.delete(row.accountId);
      });
      return next;
    });
  };

  const rowSelection = {
    selectedRowKeys: [...selectedMap.keys()],
    onSelect: (record, selected) => updateSelected([record], selected),
    onSelectAll: (selected, _selectedRows, changeRows) => updateSelected(changeRows, selected),
  };

  const handleSubmit = async () => {
    if (selectedMap.size === 0) {
      message.warning("Please select at least one user.");
      return;
    }
    setSubmitting(true);
    try {
      const response = await http.post(api.getsaveAssociation, {
        roleId: roleIdRef.current,
        ids: [...selectedMap.keys()],
      });
      if (response?.code === "1") {
        message.success("Successfully.");
        closeModal();
        fetchData(pageRef.current.current, pageRef.current.pageSize);
      } else {
        message.error(response?.msg);
      }
    } catch (err) {
      console.error("提交失败:", err);
      message.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ================= 初始化 =================
  useEffect(() => {
    const params = getRouteParams();
    roleIdRef.current = params.roleId;
    setRoleData(params);
    fetchData(1, PAGE_SIZE);
  }, []);

  // ================= 渲染 =================
  return (
    <div style={{ padding: "20px" }}>
      <Flex className="topDiv" justify="space-between" align="flex-start">
        <div>
          <span style={{ color: "#4096ff", marginRight: "10px" }}>{roleData.roleName}</span>
          <Space size="large">
            <Button className="btnCompare" onClick={openModal}>
              + Account
            </Button>
            <Button className="btnGreen" onClick={onDelete}>
              Delete
            </Button>
          </Space>
        </div>
      </Flex>

      <Table
        rowSelection={{ type: "checkbox", selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
        loading={tableLoading}
        columns={columns}
        dataSource={tableData}
        rowKey="accountId"
        pagination={{
          showQuickJumper: true,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total} items`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
        scroll={{ x: "max-content", y: "calc(100vh - 350px)" }}
        className="my-Table"
      />

      <Modal
        title={
          <Flex justify="flex-start" align="flex-start" className="model_title">
            <EditOutlined className="model_title_mr10" />
            <Typography.Title level={4} className="mg">
              SELECT USER
            </Typography.Title>
          </Flex>
        }
        width="80%"
        open={modalOpen}
        onCancel={closeModal}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              Submit
            </Button>
          </div>
        }
        styles={{
          body: {
            height: "80vh",
            padding: "16px 24px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div
          className="model_top"
          style={{ width: "100%", height: "100%", display: "flex", gap: "20px", overflow: "hidden" }}
        >
          {/* 左:未分配人员 */}
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <Table
              rowSelection={rowSelection}
              loading={otherLoading}
              columns={otherColumns}
              dataSource={otherData}
              rowKey="accountId"
              onChange={(p) => fetchOtherList(p.current, p.pageSize)}
              pagination={{
                showQuickJumper: true,
                current: otherPagination.current,
                pageSize: otherPagination.pageSize,
                total: otherPagination.total,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} items`,
              }}
              scroll={{ x: "max-content", y: "calc(80vh - 200px)" }}
              className="my-Table"
              style={{ flex: 1, overflow: "auto" }}
            />
          </div>

          {/* 右:已选人员(不受搜索影响) */}
          <div style={{ width: "500px", borderRadius: "6px", overflow: "auto", background: "#fff" }}>
            <Table
              size="small"
              pagination={false}
              dataSource={[...selectedMap.values()]}
              rowKey="accountId"
              columns={[
                { title: "Email", dataIndex: "email", width: 230 },
                { title: "Name", dataIndex: "allname", width: 150 },
                {
                  title: "Action",
                  key: "action",
                  width: 60,
                  align: "center",
                  render: (_, record) => (
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => updateSelected([record], false)}
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}